import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  LoadedContent,
  TableContainer,
  TableHeaderBulkSelectCheckbox,
} from '@occc/ui-design-system';
import { Box } from '@occc-app/design-system';
import { KioskFragment } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';

import { TouchpointKiosksTableItem } from './touchpoint-kiosks-table-item.component';

type TouchpointKiosksTableProps = {
  loading: boolean;
  error?: ApolloError;
  data?: KioskFragment[];
  paginationProps: IPaginationProps;
  bulkSelectedItems: Set<string>;
  handleBulkSelectMultiple?: (
    currentPageItems: string[],
    currentPageSelectedItems: string[],
  ) => void;
  handleItemSelect: (id: string, kiosk: KioskFragment) => void;
  handleBulkDeselectAll: () => void;
  hideBulkSelect?: boolean;
  maxSelection?: number;
};

export const TouchpointKiosksTable = ({
  loading,
  error,
  data,
  paginationProps,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleItemSelect,
  hideBulkSelect = false,
  maxSelection,
}: TouchpointKiosksTableProps) => {
  const currentPageKioskIds = data?.map((item) => item.id) || [];
  const isMaxSelectionReached = maxSelection ? bulkSelectedItems.size >= maxSelection : false;

  const renderData = (items: KioskFragment[]) => (
    <TableContainer sx={{ boxShadow: 'none' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding='checkbox'>
              {!hideBulkSelect && handleBulkSelectMultiple && (
                <TableHeaderBulkSelectCheckbox
                  bulkSelectedItems={bulkSelectedItems}
                  handleBulkSelectMultiple={handleBulkSelectMultiple}
                  currentPageItems={currentPageKioskIds}
                />
              )}
            </TableCell>
            <TableCell>Kiosk ID</TableCell>
            <TableCell>Kiosk Name</TableCell>
            <TableCell>Kiosk Address</TableCell>
            <TableCell>Phone Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((kiosk) => {
            const isSelected = bulkSelectedItems.has(kiosk.id);
            const isDisabled = isMaxSelectionReached && !isSelected;

            return (
              <TouchpointKiosksTableItem
                kiosk={kiosk}
                key={kiosk.id}
                isItemSelected={isSelected}
                isItemDisabled={isDisabled}
                handleItemSelect={handleItemSelect}
              />
            );
          })}
        </TableBody>
      </Table>
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Pagination {...paginationProps} />
      </Box>
    </TableContainer>
  );

  return (
    <LoadedContent<KioskFragment>
      error={error}
      loading={loading}
      items={data}
      renderData={renderData}
    />
  );
};
